import { TemplateResult, html } from 'lit';

import { HoldingsArgs, HoldingsTableColumn } from './holdings.model.js';
import { isNullish, isNullishOrEmptyString } from '../../common/utils/nullish';
import { Holding } from '../common/holding.model';
import { SortDirection, SortDirections } from '../../common/utils/tableSort';

export const generateHTML = (args: HoldingsArgs): TemplateResult =>
  html` <pop-holdings-component
    data-product-code="${args.productCode}"
    data-audience="${args.audience}"
    data-country="${args.country}"
    data-language="${args.language}"
    data-product-name="${args.productName}"
    data-component-object="${JSON.stringify(args.componentObject)}"
  ></pop-holdings-component>`;

export type TableData = number | string | null | undefined;

/**
 * Compare two holdings for sorting in ascending order based on the given key
 *
 * @param a The first holding to compare
 * @param b The second holding to compare
 * @param key The object key that specifies which value in the object to compare
 * @returns a number, either negative, zero, or positive
 */
export function compareTableDataAscending(
  a: Holding,
  b: Holding,
  key: string,
): number {
  const valueFromA = a[key];
  const valueFromB = b[key];

  if (
    valueFromA === valueFromB ||
    (isNullish(valueFromA) && isNullish(valueFromB))
  ) {
    // Tiebreaker is percent of portfolio, larger goes first
    if (
      typeof a.percentageTotalNetAssets === 'number' &&
      typeof b.percentageTotalNetAssets === 'number'
    ) {
      return b.percentageTotalNetAssets - a.percentageTotalNetAssets;
    }
    return 0;
  }

  if (typeof valueFromA === 'number' && typeof valueFromB === 'number') {
    return valueFromA - valueFromB;
  }

  // Null values always go last
  if (valueFromA === null || valueFromA === undefined) {
    return 1;
  }

  if (valueFromB === null || valueFromB === undefined) {
    return -1;
  }

  return valueFromA < valueFromB ? -1 : 1;
}

/**
 * Compare two holdings for sorting in descending order based on the given key
 *
 * @param a The first holding to compare
 * @param b The second holding to compare
 * @param key The object key that specifies which value in the object to compare
 * @returns a number, either negative, zero, or positive
 */
export function compareTableDataDescending(
  a: Holding,
  b: Holding,
  key: string,
): number {
  const valueFromA = a[key];
  const valueFromB = b[key];

  if (
    valueFromA === valueFromB ||
    (isNullish(valueFromA) && isNullish(valueFromB))
  ) {
    // Tiebreaker is percent of portfolio, larger goes first
    if (
      typeof a.percentageTotalNetAssets === 'number' &&
      typeof b.percentageTotalNetAssets === 'number'
    ) {
      return b.percentageTotalNetAssets - a.percentageTotalNetAssets;
    }
    return 0;
  }

  if (typeof valueFromA === 'number' && typeof valueFromB === 'number') {
    return valueFromB - valueFromA;
  }

  // Null values always go last
  if (valueFromA === null || valueFromA === undefined) {
    return 1;
  }

  if (valueFromB === null || valueFromB === undefined) {
    return -1;
  }

  return valueFromA < valueFromB ? 1 : -1;
}

/**
 * This function is used to determine which columns have data to show
 *
 * @param columns The columns to check
 * @param holdings The holdings to check
 * @returns The columns that have at least one non-null data point
 */
export function getColumnsToShow(
  columns: HoldingsTableColumn[],
  holdings: Holding[],
): HoldingsTableColumn[] {
  return columns.filter((column) => {
    return holdings?.some(
      (holding) => !isNullishOrEmptyString(holding[column.key]),
    );
  });
}

/**
 * This function filters a list of holdings to those with data that matches the query string
 *
 * @param holdings The holdings list to search
 * @param query The query that must be matched
 */
export function getHoldingsMatchingQuery(holdings: Holding[], query: string) {
  return holdings?.filter(
    (holding) =>
      holding.name?.toLowerCase().includes(query.toLowerCase()) ||
      holding.tickerSymbol?.toLowerCase().includes(query.toLowerCase()) ||
      holding.sectorName?.toLowerCase().includes(query.toLowerCase()) ||
      holding.industryName?.toLowerCase().includes(query.toLowerCase()),
  );
}

export function getNumberOfPages(listSize: number, pageSize: number) {
  return Math.ceil(listSize / pageSize);
}

export function getSortedHoldings(
  holdings: Holding[],
  key: string,
  sortDirection: SortDirection,
) {
  if (sortDirection === SortDirections.ASCENDING) {
    return [...holdings].sort((a, b) => {
      return compareTableDataAscending(a, b, key);
    });
  } else {
    return [...holdings].sort((a, b) => {
      return compareTableDataDescending(a, b, key);
    });
  }
}
